import React from "react";
import { Form, Field } from "react-final-form";
import {
  Form as SemanticForm,
  Header,
  Popup,
  Icon,
  Divider,
  Segment,
  Accordion,
  AccordionTitleProps
} from "semantic-ui-react";
import { modelTypeOptions } from "../../../../config/defaults";
import {
  basemapLegends,
  baseYearLegends,
  latitudeAndLongitudeLegends,
  measurementIdLegends,
  regionMeasurementSystemIdLegends,
  residentialVacancyRateLegends,
  spatialReferenceSystemLegends,
  templateLegends,
  zoomLegends
} from "../../../../config/legends/regionSettings";
import { RegionMetadataSlice } from "../../../../store/regionMetadata";
import { RegionsSettingsSlice } from "../../../../store/regionSettings";
import { Basemap } from "../../../../types/basemaps";
import { DateFormat, TimeFormat } from "../../../../types/dateTimeFormats";
import { MeasurementSystem } from "../../../../types/measurementSystems";
import { TextInput, SelectInput } from "../../../../utils/components";
import { exists, doesNotExist } from "../../../../utils/utils";
import DateAndTimeFormatPicker from "../../../common/DateAndTimeFormatPicker";

type AdministratorProps = {
  /** Function to call when submitting form. */
  onSubmit: (...args: any[]) => void;
  /** Region current settings. */
  regionSettings: RegionsSettingsSlice;
  /** Region current metadata. */
  regionMetadata: RegionMetadataSlice;
  /** Available measurement systems. */
  measurementSystems: MeasurementSystem[];
  /** Available basemaps. */
  basemaps: Basemap[];
  /** Available date formats. */
  dateFormats: DateFormat[];
  /** Available time formats. */
  timeFormats: TimeFormat[];
  /** Can user edit settings */
  canEdit: boolean;
  /** Are date time formats loading? */
  areDateTimeFormatsLoading: boolean;
  /** Are basemaps loading? */
  areBasemapsLoading: boolean;
  /** Are measurement systems loading? */
  areMeasurementSystemsLoading: boolean;
  /** Should form be disabled? */
  disabled?: boolean;
};

type AdministratorState = { activeIndex: number };

class Administrator extends React.Component<AdministratorProps, AdministratorState> {
  constructor(props: AdministratorProps) {
    super(props);
    this.state = {
      activeIndex: -1
    };
  }

  static defaultProps = {
    areBasemapsLoading: false,
    areDateTimeFormatsLoading: false,
    areMeasurementSystemsLoading: false,
    canEdit: false
  };

  disabledFields = [
    "spatialReferenceSystem",
    "template",
    "baseYear",
    "modelMeasurementSystemId",
    "zoom",
    "latitude",
    "longitude",
    "residentialVacancyRate"
  ];

  getBasemapOptions = (options = this.props.basemaps) =>
    exists(options)
      ? options.map(({ id, name }) => ({
          key: id,
          value: id,
          text: name
        }))
      : null;

  getInitialValues = () => {
    const {
      regionSettings: {
        baseYear,
        centroid,
        modelType,
        modules,
        mpoId,
        srs,
        zoom,
        basemapId,
        measurementSystemId,
        residentialVacancyRate,
        shortDateFormatId,
        longDateFormatId,
        shortTimeFormatId,
        longTimeFormatId
      },
      regionMetadata: { modelMeasurementSystemId }
    } = this.props;
    return {
      baseYear,
      centroid,
      modelType,
      modules,
      mpoId,
      zoom,
      spatialReferenceSystem: srs,
      template: modelType,
      latitude:
        centroid && centroid.coordinates && centroid.coordinates.length === 2
          ? centroid.coordinates[1]
          : null,
      longitude:
        centroid && centroid.coordinates && centroid.coordinates.length === 2
          ? centroid.coordinates[0]
          : null,
      basemapId,
      measurementSystemId,
      residentialVacancyRate,
      shortDateFormatId,
      longDateFormatId,
      shortTimeFormatId,
      longTimeFormatId,
      modelMeasurementSystemId
    };
  };

  getMeasurementSystemOptions = (options = this.props.measurementSystems) =>
    exists(options)
      ? options.map(({ id, title }) => ({
          key: id,
          value: id,
          text: title
        }))
      : null;

  getModelTypeOptions = (options = modelTypeOptions) =>
    exists(options)
      ? options.map(optionName => ({
          key: optionName,
          value: optionName,
          text: optionName
        }))
      : null;

  handleClick = (e: any, titleProps: AccordionTitleProps) => {
    const { index } = titleProps;
    const { activeIndex } = this.state;
    const newIndex = activeIndex === index ? -1 : index;
    this.setState({ activeIndex: newIndex as number });
  };

  hasInitialValuesChanged = (
    values: Record<string, any> | undefined,
    previousValues: Record<string, any> | undefined
  ) => JSON.stringify(values) === JSON.stringify(previousValues);

  /**
   * Render field with info icon
   *
   * @param {String} key                        - Field key
   * @param {String} name                       - Field name
   * @param {String} label                      - Field label
   * @param {String} infoContent                - Field icon tooltip content
   * @param {Array<Object> | null} options      - Options for select field
   * @param {String | null} placeholder         - Field placeholder
   * @param {Boolean | null} isLoading          - Should render field as loading?
   * @return {JSX.Element}                      - Returns SemanticForm Field
   */
  renderFieldWithInfoIcon = (
    key: string,
    name: string,
    label: string,
    infoContent: string,
    options: any[] | null = null,
    placeholder: string | undefined = undefined,
    isLoading: boolean | null = false
  ) => (
    <SemanticForm.Field>
      <label style={{ display: "inline" }}>{label}</label>
      <Popup trigger={<Icon name="info circle" color="blue" style={{ float: "right" }} />}>
        {infoContent}
      </Popup>
      <Field
        key={key}
        aria-label={`${label} input`}
        name={name}
        // @ts-ignore
        component={options === null ? TextInput : SelectInput}
        options={options}
        disabled={!this.props.canEdit || this.disabledFields.includes(name)}
        placeholder={exists(placeholder) ? placeholder : undefined}
        loading={isLoading}
      />
    </SemanticForm.Field>
  );

  validateFormValues = (values: Record<string, any>) => {
    const errors: Record<string, any> = {};
    const requiredFieldText = "This field is required";

    const requiredFields = [
      "spatialReferenceSystem",
      "template",
      "baseYear",
      "zoom",
      "basemapId",
      "measurementSystemId",
      "latitude",
      "longitude"
    ];
    requiredFields.forEach(name =>
      doesNotExist(values[name]) ? (errors[name] = requiredFieldText) : null
    );
    return errors;
  };

  render() {
    const { activeIndex } = this.state;
    const { disabled, onSubmit } = this.props;
    const initialValues = this.getInitialValues();

    return (
      <>
        <Header as="h2">Administrator settings</Header>
        <Header as="h3">Settings configurable by administrators for this region</Header>
        <Form
          onSubmit={onSubmit}
          initialValues={initialValues}
          initialValuesEqual={this.hasInitialValuesChanged}
          render={({ handleSubmit, invalid, pristine, submitting }) => {
            return (
              <Segment>
                <SemanticForm onSubmit={handleSubmit}>
                  <Accordion>
                    <Accordion.Title
                      active={activeIndex === 0}
                      index={0}
                      onClick={this.handleClick}
                    >
                      <Header fluid="true" as="h4" style={{ margin: 0 }}>
                        <Icon name="dropdown" />
                        UrbanCanvas region settings
                      </Header>
                    </Accordion.Title>
                    <Accordion.Content active={activeIndex === 0}>
                      {this.renderFieldWithInfoIcon(
                        "spatial_reference_system",
                        "spatialReferenceSystem",
                        spatialReferenceSystemLegends.label,
                        spatialReferenceSystemLegends.infoContent
                      )}
                      {this.renderFieldWithInfoIcon(
                        "template",
                        "template",
                        templateLegends.label,
                        templateLegends.infoContent,
                        this.getModelTypeOptions()
                      )}
                      {this.renderFieldWithInfoIcon(
                        "base_year",
                        "baseYear",
                        baseYearLegends.label,
                        baseYearLegends.infoContent
                      )}
                      {this.renderFieldWithInfoIcon(
                        "model_measurement_system_id",
                        "modelMeasurementSystemId",
                        measurementIdLegends.label,
                        measurementIdLegends.infoContent,
                        this.getMeasurementSystemOptions()
                      )}
                      {this.renderFieldWithInfoIcon(
                        "residential_vacancy_rate",
                        "residentialVacancyRate",
                        residentialVacancyRateLegends.label,
                        residentialVacancyRateLegends.infoContent,
                        null,
                        "None"
                      )}
                    </Accordion.Content>
                    <Divider />
                    <Accordion.Title
                      active={activeIndex === 1}
                      index={1}
                      onClick={this.handleClick}
                    >
                      <Header as="h4">
                        <Icon name="dropdown" />
                        User interface defaults
                      </Header>
                    </Accordion.Title>
                    <Accordion.Content active={activeIndex === 1}>
                      {this.renderFieldWithInfoIcon(
                        "zoom",
                        "zoom",
                        zoomLegends.label,
                        zoomLegends.infoContent
                      )}
                      <b style={{ fontSize: "13px" }}>{latitudeAndLongitudeLegends.label}</b>
                      <Popup
                        trigger={
                          <Icon name="info circle" color="blue" style={{ float: "right" }} />
                        }
                      >
                        {latitudeAndLongitudeLegends.infoContent}
                      </Popup>
                      <SemanticForm.Group widths="equal">
                        <SemanticForm.Field>
                          <label>Latitude</label>
                          <Field
                            aria-label="Latitude input"
                            key={"latitude"}
                            name={"latitude"}
                            // @ts-ignore
                            component={TextInput}
                            disabled={this.disabledFields.includes("latitude")}
                          />
                        </SemanticForm.Field>
                        <SemanticForm.Field>
                          <label>Longitude</label>
                          <Field
                            aria-label="Longitude input"
                            key={"longitude"}
                            name={"longitude"}
                            // @ts-ignore
                            component={TextInput}
                            disabled={this.disabledFields.includes("longitude")}
                          />
                        </SemanticForm.Field>
                      </SemanticForm.Group>
                      {this.renderFieldWithInfoIcon(
                        "basemap_id",
                        "basemapId",
                        basemapLegends.label,
                        basemapLegends.infoContent,
                        this.getBasemapOptions(),
                        undefined,
                        this.props.areBasemapsLoading
                      )}
                      {this.renderFieldWithInfoIcon(
                        "measurement_system_id",
                        "measurementSystemId",
                        regionMeasurementSystemIdLegends.label,
                        regionMeasurementSystemIdLegends.infoContent,
                        this.getMeasurementSystemOptions(),
                        undefined,
                        this.props.areMeasurementSystemsLoading
                      )}
                      <DateAndTimeFormatPicker
                        dateFormats={this.props.dateFormats}
                        disableAllFields={!this.props.canEdit}
                        displayAdminLegends={true}
                        isLoading={this.props.areDateTimeFormatsLoading}
                        timeFormats={this.props.timeFormats}
                      />
                    </Accordion.Content>
                    <Divider />
                  </Accordion>
                  <SemanticForm.Button
                    disabled={invalid || disabled || pristine}
                    loading={submitting}
                    type="submit"
                  >
                    Save
                  </SemanticForm.Button>
                </SemanticForm>
              </Segment>
            );
          }}
          validate={values => this.validateFormValues(values)}
        />
      </>
    );
  }
}

export default Administrator;
